import { getGroupByTable } from '../../insights/reacttable-columns.js';
import insightsColumns from '../../utils/insightsColumns';
import { shortDate } from '../../utils/formatters.js';
import i18n from '../../i18n/config';

type groupByTableFieldType = {
    accessor: string;
    Header: any;
    Cell: any;
    width?: number;
};

type Value = {
    dateRange: string;
    orderKey: string;
    exposures?: number;
};

type AllKeys = {
    [key: string]: Value[];
};

type ComparedDatesRowsProps = {
    groupBy: string[];
    valuesList: Array<Value[]>;
    dateRanges: string[];
};

const addZerosRow = (accessors, dateRanges) => values => {
    if (!values || values.length === 0) return [];
    if (values.length === 1) {
        const { dateRange } = values[0];
        const [key] = values[0].orderKey.split('_');
        const newDateRange = dateRanges.find(d => d !== dateRange);
        const val: Value = {
            dateRange: newDateRange,
            orderKey: `${key}_${newDateRange}`,
        };
        accessors.forEach(a => {
            val[a] = values[0][a];
        });
        const otherKeys = Object.keys(values[0]).filter(
            key => !['dateRange', ...accessors, 'orderKey'].includes(key),
        );
        otherKeys.forEach(key => {
            val[key] = 0;
        });

        if (dateRange === dateRanges[0]) {
            return [...values, val];
        } else {
            return [val, ...values];
        }
    }
    if (values.length >= 2) return values;
};

const columns = insightsColumns({
    showBroadcastData: true,
    showInVenueData: true,
});

export const getComparedDatesRows = ({
    valuesList,
    groupBy = [],
    dateRanges = [],
}: ComparedDatesRowsProps) => {
    const groupByTable = getGroupByTable(i18n.t.bind(i18n));
    const accessors = groupBy.map(g => {
        const groupByTableOption: groupByTableFieldType | undefined =
            groupByTable[g];
        return groupByTableOption?.accessor;
    });
    const addZeros = addZerosRow(accessors, dateRanges);
    if (groupBy.length === 0) return [];
    if (groupBy.length === 1 && groupBy.includes('date')) {
        const values = valuesList.map((vs, index) => {
            const val = vs.reduce(
                (acc, v) => {
                    columns.forEach(({ dbname: k }) => {
                        const val = v[k];
                        if (!acc[k]) {
                            acc[k] = val;
                        } else if (k === 'averageMvp') {
                            acc[k] =
                                acc?.exposures + v?.exposures > 0
                                    ? (acc[k] * acc?.exposures +
                                          val * v?.exposures) /
                                      (acc?.exposures + v?.exposures)
                                    : 0;
                        } else {
                            acc[k] += val;
                        }
                    });
                    return acc;
                },
                { exposures: 0 },
            );
            return { ...val, dateRange: dateRanges[index] };
        });
        return addZeros(values);
    }

    const rawValues = valuesList
        .map((values, index) => {
            return values.map(v => {
                const k = groupBy
                    .map(g => {
                        const { accessor }: groupByTableFieldType =
                            groupByTable[g] ? groupByTable[g] : {};
                        if (accessor) return v[accessor];
                        return '';
                    })
                    .join('-')
                    .toLowerCase()
                    .split(' ')
                    .join('-');

                const dateRange = dateRanges[index];

                const orderKey = `${k}_${dateRange}`
                    .split(' ')
                    .join('-')
                    .split('/')
                    .join('-');
                return {
                    ...v,
                    dateRange,
                    orderKey,
                };
            });
        })
        .flat(1);

    const allKeys: AllKeys = rawValues.reduce((acc, v) => {
        const { orderKey } = v;
        if (!acc[orderKey]) {
            acc[orderKey] = [v];
        } else {
            acc[orderKey].push(v);
        }
        return acc;
    }, {});

    const allValues = Object.values(allKeys)
        .map(addZeros)
        .flat(1)
        .sort((a, b) => {
            return a.orderKey.localeCompare(b.orderKey);
        });

    return allValues;
};

export const getDateRange = ({ startDate, endDate }) => {
    const dateRange = `${shortDate(startDate)} - ${shortDate(endDate)}`;
    return dateRange;
};
